import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

/**
 * Which athlete: the standard one (CC0 body, kit painted by the game) or the HD one (a generated, textured
 * player on the same skeleton, its kit repainted per team from its own texture). Both come from tools/build-athlete.mjs.
 */
export type AthleteVariant = 'standard' | 'hd';

/** The rigged athlete, its clips by game name, and its two textures. */
export interface AthleteModel {
  variant: AthleteVariant;
  scene: THREE.Group;
  clips: Map<string, THREE.AnimationClip>;
  /** Skin colour map; null if it failed to load (the players then get flat skin tones). */
  skin: THREE.Texture | null;
  /** Muscle normal map; null if it failed to load. */
  normal: THREE.Texture | null;
}

export interface AthleteFiles {
  modelUrl: string;
  skinTextureUrl: string;
  normalTextureUrl: string;
}

/**
 * The textures are separate image files, not packed in the model: GLTFLoader reads packed images through a
 * blob: fetch, which a strict page (a Claude artifact) blocks, and plain image files load anywhere.
 */
export async function loadAthleteModel(files: AthleteFiles, variant: AthleteVariant = 'standard'): Promise<AthleteModel> {
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  const [gltf, skin, normal] = await Promise.all([
    loader.loadAsync(files.modelUrl),
    loadTexture(files.skinTextureUrl, THREE.SRGBColorSpace),
    loadTexture(files.normalTextureUrl, THREE.NoColorSpace),
  ]);
  return { variant, scene: gltf.scene, clips: new Map(gltf.animations.map((c) => [c.name, c])), skin, normal };
}

async function loadTexture(url: string, colorSpace: THREE.ColorSpace): Promise<THREE.Texture | null> {
  try {
    const texture = await new THREE.TextureLoader().loadAsync(url);
    // As the model's own glTF sampler had it: no vertical flip, repeat wrapping.
    texture.flipY = false;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = colorSpace;
    return texture;
  } catch (err) {
    console.warn(`Athlete texture ${url} unavailable, using flat colours:`, err);
    return null;
  }
}
